import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { ChangesPanel } from "./ChangesPanel";
import { DiscrepancyPanel } from "./DiscrepancyPanel";
import {
  BROWSABLE,
  type CustomElement,
  type SnapshotCoverage,
  type SnapshotDetail,
  type SnapshotStatus,
  customElements,
  snapshotCoverage,
  snapshotDetail,
  tracePath,
} from "./profileClient";
import { SECTION_VIEWS, SectionTable, cell, originLabel } from "./SectionTable";

const TABS = [
  ...SECTION_VIEWS.map((view) => ({ id: view.section, label: view.label })),
  { id: "custom", label: "Custom extensions" },
  { id: "coverage", label: "Coverage and findings" },
  { id: "discrepancies", label: "Base Pack discrepancies" },
  { id: "changes", label: "Changes and requalification" },
];

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

/** Why a profile version that is not sealed or superseded shows only its source. */
const NOT_BROWSABLE: Partial<Record<SnapshotStatus, string>> = {
  loading: "is still loading; it can be browsed once it is sealed",
  withdrawn: "was withdrawn; it no longer governs and cannot be browsed",
  abandoned: "was abandoned before it was sealed; it cannot be browsed",
};

/** Where this profile version comes from: the source a consultant traces every answer back to. */
function SourcePanel({ detail }: { detail: SnapshotDetail }) {
  const s = detail.snapshot;
  const rows: [string, string][] = [
    ["Environment", `${s.environment_name} (${s.environment_role})`],
    ["Status", s.status === "superseded" ? "superseded: a later version governs" : s.status],
    ["Maximo", `${s.maximo_version} on ${s.platform}${s.database_platform ? `, ${s.database_platform}` : ""}`],
    ["Source instance", s.source_instance_id],
    ["Collection method", s.collection_method],
    ["Collection source", s.collection_source],
    ["Collected", `${s.collected_at} by ${s.collected_by}`],
    ["Coverage", s.coverage_statement ? `${s.capture_coverage}: ${s.coverage_statement}` : s.capture_coverage],
    ["Source SHA-256", s.source_sha256],
    ["Seal SHA-256", s.seal_sha256 ?? "not sealed"],
  ];
  return (
    <section aria-label="Profile source">
      <table className="grid">
        <caption>Source of this profile version</caption>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <th scope="row">{label}</th>
              <td>{value}</td>
            </tr>
          ))}
          <tr>
            <th scope="row">Installed components</th>
            <td>
              {detail.components
                .map((c) => [c.code, c.version, `(${c.kind})`].filter(Boolean).join(" "))
                .join(", ") || "none recorded"}
            </td>
          </tr>
          <tr>
            <th scope="row">Organizations and sites</th>
            <td>
              {detail.scope
                .filter((e) => e.kind === "organization" || e.kind === "site")
                .map((e) => (e.kind === "site" ? `${e.organization_code}/${e.code}` : e.code))
                .join(", ") || "none recorded"}
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

function CustomPanel({ snapshotId }: { snapshotId: string }) {
  const [elements, setElements] = useState<CustomElement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    customElements(snapshotId)
      .then((found) => {
        if (active) setElements(found);
      })
      .catch((caught: unknown) => {
        if (active) setError(message(caught));
      });
    return () => {
      active = false;
    };
  }, [snapshotId]);
  if (error) return <p role="alert">{error}</p>;
  if (!elements) return <p>Loading…</p>;
  return (
    <table className="grid">
      <caption>Customer-defined and extension elements, as profiled</caption>
      <thead>
        <tr>
          <th>Kind</th>
          <th>Element</th>
          <th>Origin</th>
          <th>Basis</th>
          <th>Scope level</th>
          <th>Support</th>
          <th>Source row</th>
        </tr>
      </thead>
      <tbody>
        {elements.map((e) => (
          <tr key={`${e.kind}-${e.source.row_key}`}>
            <td>{e.kind}</td>
            <td>
              {e.kind === "attribute" && e.objectname ? (
                <Link to={tracePath(snapshotId, e.objectname, e.name)}>
                  {e.objectname}.{e.name}
                </Link>
              ) : (
                e.name
              )}
            </td>
            <td>{originLabel(e.origin)}</td>
            <td>{e.origin.basis}</td>
            <td>{cell(e.scope_level)}</td>
            <td>{e.support_level}</td>
            <td>
              {e.source.section} {e.source.row_key}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CoveragePanel({ coverage }: { coverage: SnapshotCoverage }) {
  return (
    <>
      <table className="grid">
        <caption>Sections captured</caption>
        <thead>
          <tr>
            <th>Section</th>
            <th>Captured</th>
            <th>Rows</th>
          </tr>
        </thead>
        <tbody>
          {coverage.sections.map((s) => (
            <tr key={s.section}>
              <td>{s.section}</td>
              <td>{s.captured ? "yes" : "no: unknown, not absent"}</td>
              <td>{s.row_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="grid">
        <caption>Findings</caption>
        <thead>
          <tr>
            <th>Severity</th>
            <th>Code</th>
            <th>Section</th>
            <th>Subject</th>
            <th>Message</th>
          </tr>
        </thead>
        <tbody>
          {coverage.findings.map((f) => (
            <tr key={`${f.code}-${f.section}-${f.subject}`}>
              <td>{f.severity}</td>
              <td>{f.code}</td>
              <td>{cell(f.section)}</td>
              <td>{f.subject}</td>
              <td>{f.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/** One profile version of a customer Maximo environment: its source, every section searchable,
 * its custom extensions, its coverage, and its differences from the Maximo Base Pack. */
export function SnapshotBrowserPage() {
  const { snapshotId = "" } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<SnapshotDetail | null>(null);
  const [coverage, setCoverage] = useState<SnapshotCoverage | null>(null);
  const [tab, setTab] = useState("objects");
  const [target, setTarget] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    // The source loads for every status; the views only for a version the API can query.
    snapshotDetail(snapshotId)
      .then(async (d) => {
        const c = BROWSABLE.has(d.snapshot.status) ? await snapshotCoverage(snapshotId) : null;
        if (!active) return;
        setDetail(d);
        setCoverage(c);
      })
      .catch((caught: unknown) => {
        if (active) setError(message(caught));
      });
    return () => {
      active = false;
    };
  }, [snapshotId]);

  function trace(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const [objectname, attributename] = target.trim().toUpperCase().split(".");
    if (objectname && attributename) void navigate(tracePath(snapshotId, objectname, attributename));
  }

  if (error) return <p role="alert">{error}</p>;
  if (!detail) return <p>Loading profile version…</p>;
  const heading = (
    <>
      <p>
        <Link to="/environment-profiles">Environment profiles</Link>
      </p>
      <h1>
        {detail.snapshot.profile_key}@v{detail.snapshot.profile_version}
      </h1>
      <SourcePanel detail={detail} />
    </>
  );
  if (!coverage) {
    const why = NOT_BROWSABLE[detail.snapshot.status] ?? `is ${detail.snapshot.status}`;
    return (
      <section>
        {heading}
        <p role="status">
          This profile version {why}. Only a sealed or superseded version can be browsed, traced
          or compared with the Base Pack.
        </p>
      </section>
    );
  }
  const view = SECTION_VIEWS.find((v) => v.section === tab);
  return (
    <section>
      {heading}
      <form onSubmit={trace} aria-label="Trace a target">
        <label>
          Trace a target attribute (OBJECT.ATTRIBUTE){" "}
          <input value={target} onChange={(event) => setTarget(event.target.value)} />
        </label>{" "}
        <button type="submit">Trace</button>
      </form>
      <div role="tablist" aria-label="Profile views">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" aria-label={TABS.find((t) => t.id === tab)?.label}>
        {view && <SectionTable key={view.section} snapshotId={snapshotId} view={view} />}
        {tab === "custom" && <CustomPanel snapshotId={snapshotId} />}
        {tab === "coverage" && <CoveragePanel coverage={coverage} />}
        {tab === "discrepancies" && <DiscrepancyPanel snapshotId={snapshotId} />}
        {tab === "changes" && <ChangesPanel snapshotId={snapshotId} />}
      </div>
    </section>
  );
}
